import { useCallback, useEffect, useState } from "react";

import { useRouter } from "next/router";

import { resolveSearchParams } from "@/features/search/api/queries";
import { SEARCH_PATH } from "@/features/search/api/route";
import { useRecentSearches } from "@/features/search/hooks/useRecentSearches";
import { urlQueryFromParams } from "@/features/search/utils/search-params.utils";

const searchHref = (term: string) => ({
  pathname: SEARCH_PATH,
  query: { query: term },
});

/** The same search as params, when the service could express it that way. */
const paramsHref = (params: string) => ({
  pathname: SEARCH_PATH,
  query: urlQueryFromParams(params),
});

/**
 * Run a search: remember the term, ask what it means, then navigate. `isSubmitting` stays true until
 * the route settles, which is what the input's spinner reflects — `/search` renders server-side, so
 * there is a real wait to show, and the question below happens inside it.
 *
 * Asking first is the difference between landing on the filtered URL and landing on the sentence and
 * being moved a moment later. The results page still converts on arrival, which covers every other
 * way in — a shared link, a bookmark, the back button — so this is an improvement to the path people
 * actually take, not the only thing holding it up.
 */
export const useSearchSubmit = () => {
  const router = useRouter();
  const { addRecentTerm } = useRecentSearches();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleRouteSettled = () => setIsSubmitting(false);

    router.events.on("routeChangeComplete", handleRouteSettled);
    router.events.on("routeChangeError", handleRouteSettled);

    return () => {
      router.events.off("routeChangeComplete", handleRouteSettled);
      router.events.off("routeChangeError", handleRouteSettled);
    };
  }, [router.events]);

  const submit = useCallback(
    async (term: string) => {
      const normalized = term.trim();

      if (!normalized) {
        return;
      }

      addRecentTerm(normalized);
      setIsSubmitting(true);

      /* Null for anything the service reads as an ordinary query — most searches, and every short
       * one, which it answers without going near a model. */
      const params = await resolveSearchParams(normalized);

      return router.push(params ? paramsHref(params) : searchHref(normalized));
    },
    [addRecentTerm, router]
  );

  return { submit, isSubmitting };
};
